import React from "react";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import {
  IconChevronRight,
  IconMedicalCross,
  IconClipboardList,
  IconPills,
  IconFlask,
  IconFolders,
} from "@tabler/icons-react";
import { FamilyStats } from "@/types/familyStats";
import styles from "./FamilyOverview.module.css";

interface FamilyOverviewProps {
  familyStats: FamilyStats | null;
}

const FamilyOverview: React.FC<FamilyOverviewProps> = ({ familyStats }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!familyStats) return null;

  const rows = [
    {
      key: "emergencyCards",
      icon: <IconMedicalCross size={24} color="#4C189A" />,
      count: familyStats.totalEmergencyCardReady,
      route: "/profile/family/emergency-cards-overview",
    },
    {
      key: "healthSummaries",
      icon: <IconClipboardList size={24} color="#4C189A" />,
      count: familyStats.totalHealthSummaryReady,
      route: "/profile/family/health-summary-overview",
    },
  ];

  const miniCards = [
    {
      key: "pillbox",
      icon: <IconPills size={17} color="#8764BB" />,
      count: familyStats.totalMedicationsAdded,
      route: "/profile/family/pillbox-overview",
    },
    {
      key: "labTrends",
      icon: <IconFlask size={17} color="#8764BB" />,
      count: familyStats.totalLabInsightsReady,
      route: "/profile/family/lab-trends-overview",
    },
  ];

  return (
    <div className={styles.section} data-family-overview>
      <h2 className={styles.title}>{t("dashboard.familyOverview.title")}</h2>

      <button
        type="button"
        className={styles.heroCard}
        onClick={() =>
          navigate("/profile/family/documents-overview", {
            state: { backUrl: "/" },
          })
        }
      >
        <IconFolders className={styles.heroIllustration} stroke={1.5} />
        <span className={styles.heroCount}>{familyStats.totalDocs}</span>
        <span className={styles.heroLabelRow}>
          <span className={styles.heroLabel}>
            {t("dashboard.familyOverview.recordsAdded")}
          </span>
          <IconChevronRight size={24} color="#4C189A" />
        </span>
      </button>

      {rows.map((row) => (
        <button
          key={row.key}
          type="button"
          className={styles.statRow}
          onClick={() => navigate(row.route, { state: { backUrl: "/" } })}
        >
          <span className={styles.statRowIconWrapper}>{row.icon}</span>
          <span className={styles.statRowContent}>
            <span className={styles.statRowTextGroup}>
              <span className={styles.statRowLabel}>
                <Trans
                  i18nKey={`dashboard.familyOverview.rows.${row.key}`}
                  components={{
                    light: <span className={styles.statRowLabelLight} />,
                  }}
                />
              </span>
              <span className={styles.statRowCount}>{row.count}</span>
            </span>
            <IconChevronRight size={24} color="#4C189A" />
          </span>
        </button>
      ))}

      <div className={styles.miniGrid}>
        {miniCards.map((card) => (
          <button
            key={card.key}
            type="button"
            className={styles.miniCard}
            onClick={() => navigate(card.route)}
          >
            <div className={styles.miniCardStripe} />
            <div className={styles.miniCardBody}>
              <div className={styles.miniCardTopRow}>
                <span className={styles.miniCardIconWrapper}>{card.icon}</span>
                <span className={styles.miniCardLabel}>
                  {t(`dashboard.familyOverview.miniCards.${card.key}.label`)}
                </span>
                <IconChevronRight size={24} color="#4C189A" />
              </div>
              <div className={styles.miniCardCaption}>
                <Trans
                  i18nKey={`dashboard.familyOverview.miniCards.${card.key}.caption`}
                  count={card.count}
                  values={{ count: card.count }}
                  components={{
                    b: <b className={styles.miniCardCaptionCount} />,
                  }}
                />
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default FamilyOverview;
